import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getArticleBreadcrumb } from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { getVideoById, getRelatedVideos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = id ? getVideoById(id) : undefined;

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该视频内容可能已被移除或链接错误</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === video.category);
  const relatedVideos = getRelatedVideos(video.id, video.category);
  const breadcrumbItems = getArticleBreadcrumb(video.title, video.category);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="w-full py-8 md:py-10 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {category && (
                  <Link
                    to={`/category/${video.category}`}
                    className="px-3 py-1 bg-primary-100/70 text-primary-600 text-xs font-medium rounded-full hover:bg-primary-200/70 transition-colors whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-xs text-foreground-500">{video.date}</span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 leading-tight">
                {video.title}
              </h1>

              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <img
                    src={video.authorAvatar}
                    alt={video.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="text-sm font-medium text-foreground-700">{video.author}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万次观看` : `${video.views}次观看`}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-line"></i>
                    {video.likes >= 10000 ? `${(video.likes / 10000).toFixed(1)}万` : video.likes}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {video.duration}
                  </span>
                </div>
              </div>
            </header>

            <div className="mb-8 rounded-lg overflow-hidden border border-background-200/70">
              <div className="relative w-full aspect-video bg-background-200">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  title={`${video.title} - 国产自拍视频精选`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-foreground-900/20 group cursor-pointer">
                  <span className="w-16 h-16 flex items-center justify-center bg-primary-500/90 rounded-full text-background-50 text-2xl group-hover:bg-primary-500 transition-colors">
                    <i className="ri-play-fill ml-1"></i>
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-6 flex flex-wrap gap-2">
              {video.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-background-200/70 text-foreground-600 text-xs rounded-full">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="prose prose-sm max-w-none mb-10">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed whitespace-pre-line">
                {video.content}
              </p>
            </div>

            <div className="border-t border-background-200/70 pt-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-heading text-foreground-900">相关推荐</h2>
                {category && (
                  <Link
                    to={`/category/${video.category}`}
                    className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    更多{category.name}
                    <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                )}
              </div>
              {relatedVideos.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop>
                  {relatedVideos.map((v) => (
                    <VideoCard key={v.id} video={v} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}